import * as basket from "../eaiws/basket";
import { CalculationSheetInfo, PricingProcedureInfo } from "../eaiws/basket";
import { ListenableEvent } from "../utils";
import { ERR_INVALID_ARGUMENT, ERR_INVALID_STATE, newError } from "../utils/error";
import { compare, isNotNullOrEmpty } from "../utils/string";
import { ItemUpdateFlags } from "./BaseItem";
import { ArticleItem } from "./ArticleItem";
export class CalculationManager {
    /**
     * The currently active pricing procedure.
     */
    get pricingProcedure() {
        return (this.mPricingProcedure);
    }
    get currency() {
        return (this.mCurrency);
    }
    constructor(pBasket) {
        /** Will be triggered if the tax scheme has changed. */
        this.eventTaxSchemeChanged = new ListenableEvent(this);
        /** Will be triggered if the currency has changed. */
        this.eventCurrencyChanged = new ListenableEvent(this);
        this.mBasket = pBasket;
        this.mPricingProcedure = null;
        this.mTaxSchemeId = null;
        this.mTaxScheme = null;
        this.mDefaultTaxScheme = null;
        this.mCurrency = "";
        this.mReferenceCurrency = null;
        this.mExchangeRates = null;
    }
    async activatePricingProcedure(pPricingProcedureName) {
        if (this.mPricingProcedure != null && this.mPricingProcedure.name === pPricingProcedureName)
            return;
        const tSession = this.mBasket.session;
        this.mPricingProcedure = null;
        //add to project if necessary
        if (!await this.isPriceProcActive(pPricingProcedureName)) {
            await tSession.basket.addPriceCalculation(pPricingProcedureName);
        }
        this.mPricingProcedure = await PricingProcedureInfo.CreateInfo(tSession, pPricingProcedureName);
        this.mBasket.invalidateHeaderCalculation();
    }
    async setCurrency(pCurrency) {
        if (this.mCurrency == pCurrency)
            return;
        await this.mBasket.session.basket.setCurrency(pCurrency);
        this.mCurrency = pCurrency;
        //reset UpToDate states back to Updatable, because article price may change with the new currency
        this.resetUpToDateOfmlUpdateState(this.mBasket.root);
        //force full update of views, EAIWS seems to rebuild the views internally after the currency has changed
        await this.mBasket._updateViewItems(this.mBasket.root, ItemUpdateFlags.Item | ItemUpdateFlags.IncludeChildren);
        this.eventCurrencyChanged.trigger();
        return (this.notifyCalculationChange(null));
    }
    async getTaxSchemeId() {
        if (this.mTaxSchemeId != null)
            return (this.mTaxSchemeId);
        this.mTaxSchemeId = await this.mBasket.session.basket.getCurrentTaxScheme();
        return (this.mTaxSchemeId);
    }
    async setTaxSchemeId(pSchemeId) {
        if (this.mTaxSchemeId === pSchemeId)
            return;
        await this.mBasket.session.basket.selectCurrentTaxScheme(pSchemeId);
        this.mTaxSchemeId = pSchemeId;
        this.mTaxScheme = null;
        this.mDefaultTaxScheme = null;
        //check if tax scheme has fixed currency
        const tNewScheme = await this.getTaxScheme();
        if (isNotNullOrEmpty(tNewScheme.currency) && tNewScheme.currency !== this.mCurrency) {
            this.mCurrency = tNewScheme.currency;
            this.eventCurrencyChanged.trigger();
        }
        this.eventTaxSchemeChanged.trigger();
        //update item properties because items may get inconsitence
        return (this.notifyCalculationChange(null, true, true));
    }
    async getTaxScheme() {
        if (this.mTaxScheme != null)
            return (this.mTaxScheme);
        const tId = await this.getTaxSchemeId();
        this.mTaxScheme = await this.mBasket.session.basket.getTaxScheme(true, tId);
        return (this.mTaxScheme);
    }
    async getDefaultTaxScheme() {
        if (this.mDefaultTaxScheme != null)
            return (this.mDefaultTaxScheme);
        const tId = await this.getTaxSchemeId();
        this.mDefaultTaxScheme = await this.mBasket.session.basket.getTaxScheme(false, tId);
        return (this.mDefaultTaxScheme);
    }
    async setTaxRate(pType, pCategory, pRate) {
        const tSchemeId = await this.getTaxSchemeId();
        await this.mBasket.session.basket.setTaxRate(tSchemeId, pType, pCategory, pRate);
        this._notifyTaxSchemeChanged();
        return (this.notifyCalculationChange(null));
    }
    async resetTaxRate(pType, pCategory) {
        const tSchemeId = await this.getTaxSchemeId();
        await this.mBasket.session.basket.resetTaxRate(tSchemeId, pType, pCategory);
        this._notifyTaxSchemeChanged();
        return (this.notifyCalculationChange(null));
    }
    async resetTaxScheme() {
        const tSchemeId = await this.getTaxSchemeId();
        await this.mBasket.session.basket.resetTaxScheme(tSchemeId);
        this._notifyTaxSchemeChanged();
        return (this.notifyCalculationChange(null));
    }
    async getExchangeRates() {
        if (this.mExchangeRates != null)
            return (this.mExchangeRates);
        this.mExchangeRates = await this.mBasket.session.basket.getExchangeRates();
        //sort currencies alphabetically
        this.mExchangeRates.sort((a, b) => compare(a.currency, b.currency));
        return (this.mExchangeRates);
    }
    async setExchangeRates(pRates) {
        if (pRates.length === 0)
            return;
        await this.mBasket.session.basket.setExchangeRates(pRates);
        this.mExchangeRates = null; //TODO: maybe update exchange rates here instead of requesting again from server
        return (this.notifyCalculationChange(null));
    }
    async getReferenceCurrency() {
        if (this.mReferenceCurrency != null)
            return (this.mReferenceCurrency);
        this.mReferenceCurrency = await this.mBasket.session.basket.getReferenceCurrency();
        return (this.mReferenceCurrency);
    }
    async setConditionDescription(pCondType, pLang, pDescription) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        await this.mBasket.session.basket.setConditionDescription(this.mPricingProcedure.name, pCondType, pLang, pDescription);
        return (this.notifyCalculationChange(null));
    }
    async setItemConditionDescription(pItems, pCondType, pCounter, pLang, pDescription) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.setItemConditionDescription(tItemIds, this.mPricingProcedure.name, pCondType, pCounter, pLang, pDescription, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    async setConditionAmount(pItems, pCondType, pCounter, pAmount, pCurrency) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.setConditionAmount(tItemIds, this.mPricingProcedure.name, pCondType, pCounter, pAmount, pCurrency, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    async setQuantityRelation(pItems, pCondType, pCounter, pAmount, pUnit) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.setQuantityRelation(tItemIds, this.mPricingProcedure.name, pCondType, pCounter, pAmount, pUnit, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    async adjustConditionValue(pItems, pCondType, pCounter, pValue, pCurrency) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.adjustConditionValue(tItemIds, this.mPricingProcedure.name, pCondType, pCounter, pValue, pCurrency, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    async adjustCalculationLineAmount(pItems, pLevel, pCounter, pAmount, pCurrency) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.adjustCalculationLineAmount(tItemIds, this.mPricingProcedure.name, pLevel, pCounter, pAmount, pCurrency, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    async adjustCalculationLineValue(pItems, pLevel, pCounter, pValue, pCurrency) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.adjustCalculationLineValue(tItemIds, this.mPricingProcedure.name, pLevel, pCounter, pValue, pCurrency, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    async resetConditionAmount(pItems, pCondType, pCounter) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.resetConditionAmount(tItemIds, this.mPricingProcedure.name, pCondType, pCounter, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    /**
     * Updates the default value of all conditions (in case it has changed) for the given items.
     * If an amount was overridden manually, it will not be changed.
     */
    async updateConditionAmounts(pItems) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems.map((pItem) => pItem.id);
        if (tItemIds.length > 0) {
            const tOptions = new basket.UpdateBasketArticlesOptions();
            tOptions.subItems = this.containesSingleFolder(pItems); //see: getSetItemCalculationOptions
            tOptions.migrate = false;
            tOptions.update = false;
            tOptions.recalculate = true;
            await this.mBasket.session.basket.updateBasketArticles(tItemIds, null, tOptions);
            return (this.notifyCalculationChange(pItems));
        }
    }
    async resetQuantityRelation(pItems, pCondType, pCounter) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.resetQuantityRelation(tItemIds, this.mPricingProcedure.name, pCondType, pCounter, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    async addManualCondition(pItems, pCondType) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.addManualCondition(tItemIds, this.mPricingProcedure.name, pCondType, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    async removeCondition(pItems, pCondType, pCounter) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tItemIds = pItems != null ? pItems.map((pItem) => pItem.id) : [""];
        if (tItemIds.length > 0) {
            await this.mBasket.session.basket.removeCondition(tItemIds, this.mPricingProcedure.name, pCondType, pCounter, this.getSetItemCalculationOptions(pItems));
            return (this.notifyCalculationChange(pItems));
        }
    }
    /**
     * Returns a group calculation for the given items.
     * Note: Group calculations are not cached internally and will be requested everytime from EAIWS.
     */
    getGroupCalculation(pItems) {
        if (this.mPricingProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        if (pItems.length < 2)
            throw newError(ERR_INVALID_ARGUMENT, "2 or more items required");
        const tOptions = new basket.GetPriceCalculationSheetOptions();
        //include price of alternative/optional items
        tOptions.excludeInactiveItems = false;
        tOptions.includeInactiveItems = true;
        const tPromise = this.mBasket.session.basket.getPriceCalculationSheet(pItems.map((pItem) => pItem.id), this.mPricingProcedure.name, tOptions).then((pSheet) => {
            return (new CalculationSheetInfo(this.mPricingProcedure, pSheet));
        });
        return (tPromise);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _clear() {
        this.mPricingProcedure = null;
        this.mTaxSchemeId = null;
        this.mTaxScheme = null;
        this.mDefaultTaxScheme = null;
        this.mCurrency = "";
        this.mReferenceCurrency = null;
        this.mExchangeRates = null;
    }
    /** @internal */
    async _loadFromSession(pPricingProcedureName) {
        const tPromises = new Array();
        //get currency
        tPromises.push(this.mBasket.session.basket.getCurrency().then((pCurrency) => {
            this.mCurrency = pCurrency;
        }));
        //activate pricing procedure
        if (isNotNullOrEmpty(pPricingProcedureName)) {
            tPromises.push(this.activatePricingProcedure(pPricingProcedureName));
        }
        await Promise.all(tPromises);
    }
    /** @internal */
    _notifyTaxSchemeChanged() {
        this.mTaxScheme = null;
        this.eventTaxSchemeChanged.trigger();
    }
    isPriceProcActive(pPPName) {
        const tPromise = this.mBasket.session.basket.listPricingProcedures(true).then((pList) => {
            for (const tProc of pList) {
                if (tProc.name === pPPName)
                    return (true);
            }
            return (false);
        });
        return (tPromise);
    }
    async notifyCalculationChange(pItems, pUpdateItemProps = false, pUpdateTax = false) {
        let tFlags = ItemUpdateFlags.Calculation;
        if (pUpdateItemProps)
            tFlags |= ItemUpdateFlags.ItemProps;
        if (pUpdateTax)
            tFlags |= ItemUpdateFlags.Tax;
        try {
            this.mBasket._beginItemUpdate();
            if (pItems == null) {
                //header calculation changed
                await this.mBasket.root.updateItem(tFlags | ItemUpdateFlags.IncludeChildren);
                this.mBasket.invalidateHeaderCalculation();
            }
            else {
                //item calculation changed
                const tUpdatePromises = new Array();
                for (const tItem of pItems) {
                    if (tItem.isGroup) {
                        tFlags |= ItemUpdateFlags.IncludeChildren;
                    }
                    tUpdatePromises.push(tItem.updateItem(tFlags));
                }
                await Promise.all(tUpdatePromises);
            }
        }
        finally {
            this.mBasket._endItemUpdate();
        }
    }
    resetUpToDateOfmlUpdateState(pParent) {
        for (const tChild of pParent.children) {
            if (tChild instanceof ArticleItem && tChild.ofmlUpdateState === "UpToDate") {
                tChild._setOfmlUpdateState("Updatable", true);
            }
            this.resetUpToDateOfmlUpdateState(tChild);
        }
    }
    containesSingleFolder(pItems) {
        return (pItems != null && pItems.length === 1 && pItems[0].type === "Folder");
    }
    getSetItemCalculationOptions(pItems) {
        const tOptions = new basket.CondGroupSelectionOptions();
        tOptions.subItems = this.containesSingleFolder(pItems);
        //ensure correct price for alternative/optional articles
        tOptions.excludeInactiveItems = false;
        tOptions.includeInactiveItems = true;
        return (tOptions);
    }
}
